import { Dictionary, EntityId } from '@reduxjs/toolkit';
import { unwrap } from '@ff-os/shared-utils/unwrap';
import { wrap } from '@ff-os/shared-utils/wrap';
import { GiftJson } from '@ff-scene-plugins/gift-shared/GiftJson';
import { omit, uniq } from 'lodash';

export const migratePinnedVariablesAndInputs = (
    currentProject: GiftJson.Project,
    startupProject: GiftJson.Project
) => {

    const startupProjectPinnedIds = unwrap(startupProject.variables)
        .filter(v => v.pinned)
        .map(v => v.id);
    const currentProjectPinnedIds = unwrap(currentProject.variables)
        .filter(v => v.pinned)
        .map(v => v.id);

    const idsToAdd = startupProjectPinnedIds.filter(id => !currentProjectPinnedIds.includes(id));
    const idsToRemove = currentProjectPinnedIds.filter(id => !startupProjectPinnedIds.includes(id));
    const idsToReset = startupProjectPinnedIds.filter(id => isChoiceRemoved(id, currentProject, startupProject));

    let project = currentProject;

    project = removeVariablesAndInputsFromProject(project, idsToRemove);
    project = addVariablesAndInputsToProject(project, idsToAdd, startupProject);
    project = resetPinnedVariablesAndInputs(project, idsToReset, startupProject);

    return project;
};

const isChoiceRemoved = (
    id: EntityId,
    currentProject: GiftJson.Project,
    startupProject: GiftJson.Project
) => {

    const input = currentProject.inputs.entities[id];
    if (!input) {
        return false;
    }

    const variable = startupProject.variables.entities[id]!;
    const choices = variable.options.choices.map((c: any) => c.value);
    return !choices.includes(input.value.value);
};

const removeVariablesAndInputsFromProject = (
    currentProject: GiftJson.Project,
    idsToRemove: string[]
): GiftJson.Project => ({
    ...currentProject,
    inputs: {
        ids: currentProject.inputs.ids.filter(id => !idsToRemove.includes(id as string)),
        entities: omit(currentProject.inputs.entities, idsToRemove)
    },
    variables: {
        ids: currentProject.variables.ids.filter(id => !idsToRemove.includes(id as string)),
        entities: omit(currentProject.variables.entities, idsToRemove)
    }
});

const addVariablesAndInputsToProject = (
    currentProject: GiftJson.Project,
    idsToAdd: string[],
    startupProject: GiftJson.Project
): GiftJson.Project => {

    const toEntitiesToAdd = (entities: Dictionary<any>) => wrap(idsToAdd.map(id => entities[id]!)).entities;

    return {
        ...currentProject,
        inputs: {
            ids: uniq(currentProject.inputs.ids.concat(idsToAdd)),
            entities: {
                ...currentProject.inputs.entities,
                ...toEntitiesToAdd(startupProject.inputs.entities)
            }
        },
        variables: {
            ids: uniq(currentProject.variables.ids.concat(idsToAdd)),
            entities: {
                ...currentProject.variables.entities,
                ...toEntitiesToAdd(startupProject.variables.entities)
            }
        }
    };
};

const resetPinnedVariablesAndInputs = (
    currentProject: GiftJson.Project,
    idsToReset: string[],
    startupProject: GiftJson.Project
): GiftJson.Project => {

    const currentProjectPinnedIds = unwrap(currentProject.variables)
        .filter(v => v.pinned)
        .map(v => v.id);

    const variablesToOverride = wrap(
        currentProjectPinnedIds.map(id => startupProject.variables.entities[id]!)).entities;
    const inputsToOverride = wrap(idsToReset.map(id => startupProject.inputs.entities[id]!)).entities;

    return {
        ...currentProject,
        inputs: {
            ...currentProject.inputs,
            entities: {
                ...currentProject.inputs.entities,
                ...inputsToOverride
            }
        },
        variables: {
            ...currentProject.variables,
            entities: {
                ...currentProject.variables.entities,
                ...variablesToOverride
            }
        }
    };
};